import React, { useEffect, useState } from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { fetchEmailSettingsApi, updateEmailSettingsApi } from "../../http/userAPI";
import {
    EMAIL_NOTIFICATION_GROUPS,
    EMAIL_NOTIFICATION_KEYS,
    mergeEmailNotificationSettings,
} from "../../helpers/emailNotificationSettings";

const buildBulkSettings = (value) => EMAIL_NOTIFICATION_KEYS.reduce((acc, key) => {
    acc[key] = value;
    return acc;
}, {});

const EmailNotifications = () => {
    const [settings, setSettings] = useState(mergeEmailNotificationSettings());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                const { settings: serverSettings } = await fetchEmailSettingsApi();
                if (mounted) {
                    setSettings(mergeEmailNotificationSettings(serverSettings));
                }
            } catch (e) {
                toast.error("Failed to load e-mail settings", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setSettings((prev) => ({ ...prev, [name]: checked }));
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            const nextSettings = mergeEmailNotificationSettings(settings);
            const data = await updateEmailSettingsApi(nextSettings);
            setSettings(mergeEmailNotificationSettings(data.settings));
            toast.success("Notification Settings Saved!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } catch (e) {
            toast.error("Save failed", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setSaving(false);
        }
    };

    const allSelected = EMAIL_NOTIFICATION_KEYS.every((key) => settings[key] === true);
    const allDeselected = EMAIL_NOTIFICATION_KEYS.every((key) => settings[key] === false);
    const disabled = loading || saving;

    return (
        <Card className="p-3 w-100 mt-0">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                <h5 className="text-muted mb-2 mb-md-0">
                    Email Notifications Settings
                </h5>
                {disabled && <Spinner animation="border" size="sm" />}
            </div>

            <small className="text-muted d-block mt-2">
                In this section you can customize which email notifications you want to
                receive from Virtalibry. Notifications help you stay on top of events
                related to your assignments, reviews, product updates, and recommendations.
                Each setting works independently.
                <br />
                <br />
                Select or deselect all notifications with a single click, or adjust them
                individually below:
            </small>

            <div className="mt-3">
                <Form.Check
                    className="custom-checkbox mb-1 d-flex align-items-center"
                    type="checkbox"
                    label={<small className="ms-2">Select All Notifications</small>}
                    checked={allSelected}
                    onChange={() => setSettings(buildBulkSettings(true))}
                    disabled={disabled}
                />

                <Form.Check
                    className="custom-checkbox mb-3 d-flex align-items-center"
                    type="checkbox"
                    label={<small className="ms-2">Deselect All Notifications</small>}
                    checked={allDeselected}
                    onChange={() => setSettings(buildBulkSettings(false))}
                    disabled={disabled}
                />

                {EMAIL_NOTIFICATION_GROUPS.map((group) => (
                    <div key={group.id} className="mb-3">
                        <div className="fw-semibold text-muted small mb-1">{group.title}</div>
                        {group.items.map((item) => (
                            <Form.Check
                                key={item.key}
                                className="custom-checkbox mb-1 d-flex align-items-center"
                                type="checkbox"
                                label={<small className="ms-2">{item.title}</small>}
                                name={item.key}
                                checked={settings[item.key] === true}
                                onChange={handleCheckboxChange}
                                disabled={disabled}
                            />
                        ))}
                    </div>
                ))}
            </div>

            <div className="d-flex justify-content-end mt-3">
                <Button
                    size="sm"
                    variant="outline-success"
                    onClick={handleSaveSettings}
                    disabled={disabled}
                >
                    {saving ? <Spinner animation="border" size="sm" /> : "Save Settings"}
                </Button>
            </div>
        </Card>
    );
};

export default EmailNotifications;
